import React, { useEffect, useState, useMemo } from 'react';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TabComponent } from '@sde/widgets-core/src/common/TabComponent';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { Card } from '@uhg-abyss/web/ui/Card';
import { useProviderDemographicsQuery } from '@sde/widgets-core/src/common/hooks/useProviderDemographicsQuery';
import PropTypes from 'prop-types';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { RightRailSubCard } from 'src/common/RightRailSubCard';
import { ClaimSearch } from './ClaimSearch';
import { PraSearch } from './PraSearch';
import { homeContentLinksWithHealthPlanFormatter } from '../helpers';
import { getDomain } from 'src/tools/getDomain';

const StyledContainer = styled('div', {
  marginRight: '0px auto 0px',
});

const Divider = styled('div', {
  width: '100%',
  borderBottom: '1px solid $gray4',
});

const StyledLabel = styled('label', {
  color: '$primary1',
  fontSize: '24px !important',
  fontWeight: '700 !important',
  minWidth: 150,
  display: 'inline-block',
});

const StyledRadioButton = styled(RadioGroup.Radio, {
  display: 'flex',
});

const TopMarginDiv = styled('div', {
  marginTop: 8,
});

const StyledRadioGroup = styled(RadioGroup, {
  display: 'flex',
  justifyContent: 'space-between',
  '&.abyss-radio-group-container': {
    position: 'relative',
    width: '100%',
    margin: '10px 0',
  },
});

const HOME_PAGE_CONTENTS_LAGOON_MAP = [
  'TOOLRESOURCES',
  'INTERACTIVETRAININGGUIDE',
  'HEALTHPLANS',
  'ELECTRONICPAYMENTSOLUTIONS',
];

const CLAIMS_TAB_PROPS = {
  isClaimTab: true,
  isClaimFlaggedList: true,
};

const ClaimsTabComponent = ({ user, provider, payer, payers }) => {
  const [radioValue, setRadioValue] = useState('claims');

  useEffect(() => {
    setRadioValue('claims');
  }, []);

  return (
    <React.Fragment>
      <StyledContainer>
        <StyledLabel>Select Task</StyledLabel>
        <StyledRadioGroup
          id="select-taskrange-type"
          display="row"
          value={radioValue}
          onChange={(e) => setRadioValue(e.target.value)}
        >
          <StyledRadioButton label="Look Up a Claim" value="claims" />
          <StyledRadioButton
            label="Search Single PRA / Payment Document"
            value="prasearch"
          />
        </StyledRadioGroup>
      </StyledContainer>
      <TopMarginDiv>
        <ClaimSearch
          radioValue={radioValue}
          user={user}
          payer={payer}
          provider={provider}
          payers={payers}
        />
        <PraSearch
          radioValue={radioValue}
          user={user}
          payer={payer}
          provider={provider}
          payers={payers}
        />
      </TopMarginDiv>
    </React.Fragment>
  );
};

const RightRailComponent = React.memo(
  ({ user, toggleModal }) => {
    const [response, getProviderDemographics] = useProviderDemographicsQuery({});
    const { uuid, provider } = user;
    const { data, loading } = response;
    const { corporateProviderMpin, taxIdNumber } = provider;
    const isNewEstimator = getFeature('SDE_CLAIM_ESTIMATOR');

    const {
      getHomePageContentsByArrayOfKeys,
      getHeaderContentByKey,
      getAdditionalToolsContentsByKey,
    } = usePPDLagoon();


    useEffect(() => {
      if (uuid && taxIdNumber && corporateProviderMpin) {
        getProviderDemographics({
          variables: {
            uuid,
            tin: taxIdNumber,
            corporateProviderMpin,
          },
        });
      }
    }, [uuid, corporateProviderMpin, taxIdNumber]);

    const claimsLinks = homeContentLinksWithHealthPlanFormatter(
      getHomePageContentsByArrayOfKeys(HOME_PAGE_CONTENTS_LAGOON_MAP),
      data
    );

    const lagoonData = useMemo(
      () => ({
        optumPay: getHeaderContentByKey('OPTUMPAY'),
        umr: getAdditionalToolsContentsByKey('UMR_LINK'),
        claimEstimator: getHeaderContentByKey('UNITEDHEALTHCARECLAIMESTIMATOR'),
        sdeClaimEstimator: getHeaderContentByKey(
          'UNITEDHEALTHCARECLAIMESTIMATORSDE'
        ),
        directConnect: getHeaderContentByKey('DIRECTCONNECT'),
      }),
      [getHeaderContentByKey, getAdditionalToolsContentsByKey]
    );

    const lagoonClaimEstimatorData = isNewEstimator
      ? lagoonData.sdeClaimEstimator
      : lagoonData.claimEstimator;

    const quickLinks = useMemo(
      () => [
        {
          text: 'Optum Pay',
          link: lagoonData.optumPay.href,
          ...lagoonData.optumPay,
        },
        {
          text: 'UMR',
          link: lagoonData.umr.link,
          href: lagoonData.umr.link,
          ...lagoonData.umr,
        },
        {
          text: isNewEstimator
            ? 'Claim Estimator'
            : 'UnitedHealthcare Claim Estimator',
          link: isNewEstimator
            ? lagoonData.sdeClaimEstimator.href
            : lagoonData.claimEstimator.href,
          ...lagoonClaimEstimatorData,
        },
        {
          text: 'Direct Connect',
          link: lagoonData.directConnect.href,
          ...lagoonData.directConnect,
        },
      ],
      [lagoonData, lagoonClaimEstimatorData, isNewEstimator]
    );

    return (
      <LoadingOverlay
        isLoading={loading}
        loadingMessage="Retrieving Health Plans"
        isOpen={loading}
        css={{
          'abyss-loading-overlay-root': {
            overflow: 'hidden',
          },
        }}
      >
        <Card css={{ height: '600px', border: 'none' }}>
          <Flex
            style={{ paddingLeft: '18px', paddingRight: '18px', height: '45%' }}
            direction="column"
            justify="space-evenly"
          >
            <RightRailSubCard
              railHeader="Claims & Payments Resources"
              railLinks={claimsLinks}
              user={user}
              toggleModal={toggleModal}
              loading={loading}
            />
          </Flex>
          <Divider />
          <Flex
            style={{ paddingLeft: '18px', paddingRight: '18px', height: '53%' }}
            direction="column"
            justify="space-evenly"
          >
            <RightRailSubCard
              railHeader="Quick Links & Tools"
              railLinks={quickLinks}
              hideDivider
              user={user}
              toggleModal={toggleModal}
            />
          </Flex>
        </Card>
      </LoadingOverlay>
    );
  }
);

export const claimsRightRails = (
  user,
  provider,
  setIsErrorModalOpen,
  toggleModal,
) => {
  const domain = getDomain('sde-claims', 3200);
  return [
    <RightRailComponent
      user={user}
      toggleModal={toggleModal}
    />,
    <abyss-parcel
      scriptdomain={domain}
      import="claims-flagged-claims-list@dev"
      component="FlaggedClaimsList"
      setIsErrorModalOpen={setIsErrorModalOpen}
      showLoading
      label="Recent Flagged Claims"
      props={JSON.stringify(CLAIMS_TAB_PROPS)}
      user={JSON.stringify(user)}
      provider={JSON.stringify(provider)}
    />,
  ];
};

export const ClaimsTab = ({
  user,
  setIsErrorModalOpen,
  toggleModal,
  provider,
  payer,
  payers,
}) => {

  return (
    <TabComponent
      component={
        <ClaimsTabComponent
          user={user}
          provider={provider}
          payer={payer}
          payers={payers}
        />
      }
      rightRails={claimsRightRails(
        user,
        provider,
        setIsErrorModalOpen,
        toggleModal,
      )}
      tabName="Claims & Payments"
    />
  );
};

ClaimsTab.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  setIsErrorModalOpen: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  provider: PropTypes.objectOf(PropTypes.any).isRequired,
  payer: PropTypes.objectOf(PropTypes.any),
};

RightRailComponent.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleModal: PropTypes.func.isRequired,
};
